import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import {
  cancelSubscription,
  changeSubscriptionPlan,
  confirmRevision,
  confirmSubscription,
  getBillingGateResult,
  getBillingSummary,
  getEntitlementSnapshot,
  retryPayment,
  startSubscription,
} from '../services/billing.service';
import type { SupabaseClient } from '../supabase/client';
import type {
  BillingSummary,
  ChangeSubscriptionInput,
  ChangeSubscriptionResult,
  ConfirmRevisionInput,
  ConfirmSubscriptionInput,
  EntitlementGate,
  EntitlementGateResult,
  EntitlementSnapshot,
  StartSubscriptionInput,
  StartSubscriptionResult,
} from '../types/billing';

export const billingKeys = {
  all: ['billing'] as const,
  summary: (storeId: string) => ['billing', 'summary', storeId] as const,
  entitlement: (storeId: string) => ['billing', 'entitlement', storeId] as const,
  gate: (storeId: string, gate: EntitlementGate) => ['billing', 'gate', storeId, gate] as const,
};

function useBillingInvalidation(): (storeId: string) => void {
  const queryClient = useQueryClient();
  return (storeId) => {
    void queryClient.invalidateQueries({ queryKey: billingKeys.all });
    void queryClient.invalidateQueries({ queryKey: ['promotion-kit'] });
    void queryClient.invalidateQueries({ queryKey: ['products', storeId] });
    void queryClient.invalidateQueries({ queryKey: ['offer-campaigns', storeId] });
    void queryClient.invalidateQueries({ queryKey: ['promotional-ads', storeId] });
  };
}

function requireStoreId(storeId: string | null): string {
  if (!storeId) throw new Error('Billing query requires a store id');
  return storeId;
}

export function useBillingSummary(args: {
  readonly client: SupabaseClient;
  readonly storeId: string | null;
}): UseQueryResult<BillingSummary> {
  return useQuery({
    queryKey: args.storeId ? billingKeys.summary(args.storeId) : billingKeys.all,
    enabled: !!args.storeId,
    queryFn: () => getBillingSummary(args.client, requireStoreId(args.storeId)),
    staleTime: 15_000,
  });
}

export function useStoreEntitlements(args: {
  readonly client: SupabaseClient;
  readonly storeId: string | null;
}): UseQueryResult<EntitlementSnapshot> {
  return useQuery({
    queryKey: args.storeId ? billingKeys.entitlement(args.storeId) : billingKeys.all,
    enabled: !!args.storeId,
    queryFn: () => getEntitlementSnapshot(args.client, requireStoreId(args.storeId)),
    staleTime: 15_000,
  });
}

export function useBillingGateResult(args: {
  readonly client: SupabaseClient;
  readonly storeId: string | null;
  readonly gate: EntitlementGate;
}): UseQueryResult<EntitlementGateResult> {
  return useQuery({
    queryKey: args.storeId ? billingKeys.gate(args.storeId, args.gate) : billingKeys.all,
    enabled: !!args.storeId,
    queryFn: () => getBillingGateResult(args.client, requireStoreId(args.storeId), args.gate),
    staleTime: 15_000,
  });
}

export function useStartSubscription(
  client: SupabaseClient,
): UseMutationResult<StartSubscriptionResult, unknown, StartSubscriptionInput> {
  const invalidate = useBillingInvalidation();
  return useMutation({
    mutationFn: (input) => startSubscription(client, input),
    onSuccess: (_, input) => invalidate(input.storeId),
  });
}

export function useConfirmSubscription(
  client: SupabaseClient,
): UseMutationResult<void, unknown, ConfirmSubscriptionInput> {
  const invalidate = useBillingInvalidation();
  return useMutation({
    mutationFn: (input) => confirmSubscription(client, input),
    onSuccess: (_, input) => invalidate(input.storeId),
  });
}

export function useCancelSubscription(
  client: SupabaseClient,
): UseMutationResult<void, unknown, string> {
  const invalidate = useBillingInvalidation();
  return useMutation({
    mutationFn: (storeId) => cancelSubscription(client, storeId),
    onSuccess: (_, storeId) => invalidate(storeId),
  });
}

export function useChangeSubscriptionPlan(
  client: SupabaseClient,
): UseMutationResult<ChangeSubscriptionResult, unknown, ChangeSubscriptionInput> {
  const invalidate = useBillingInvalidation();
  return useMutation({
    mutationFn: (input) => changeSubscriptionPlan(client, input),
    onSuccess: (_, input) => invalidate(input.storeId),
  });
}

export function useConfirmRevision(
  client: SupabaseClient,
): UseMutationResult<void, unknown, ConfirmRevisionInput> {
  const invalidate = useBillingInvalidation();
  return useMutation({
    mutationFn: (input) => confirmRevision(client, input),
    onSuccess: (_, input) => invalidate(input.storeId),
  });
}

export function useRetryPayment(client: SupabaseClient): UseMutationResult<void, unknown, string> {
  const invalidate = useBillingInvalidation();
  return useMutation({
    mutationFn: (storeId) => retryPayment(client, storeId),
    onSuccess: (_, storeId) => invalidate(storeId),
  });
}
